import {
  drawToken,
  tokensThatEatTime,
  promptMarker,
  drawInitialLoadingBar,
  crossed,
  computeNextScroll,
  timeToRenderLine,
  baselineTimePerLine,
  backgroundColors,
  shouldReservePromptRow,
  computeMaxVisibleLines,
  selectLinesToRender,
  buildCumulativeTimes,
  computeLineBudgetMs,
} from '@hackvegas-2025/shared';
import { playBootupSound, playClickSound, playQuietTypingSound } from './audio';
import { state } from './state';
import { commands } from './commands';
import { attemptCommand, terminalPrompt } from './input';

export const textHeight = 20;
export const loadTime = 1000;

let previousTime = 0;
export function updateAndDrawTerminal(
  canvas: OffscreenCanvas,
  ctx: OffscreenCanvasRenderingContext2D,
  webglCanvas: HTMLCanvasElement,
) {
  const now = performance.now();
  const cycleIntervalMs = 100;
  const cycleTick = Math.floor(now / cycleIntervalMs);
  const dt = now - previousTime;
  previousTime = now;

  const previousClock = state.startupClockMs;
  const nextClock = state.focused ? previousClock + dt : previousClock;

  // startup thresholds (ms)
  const bootProgressMs = loadTime;
  const bootHoldMs = 250;
  const helpDelayMs = 200;

  const shouldPlayBootSound = crossed(previousClock, nextClock, bootProgressMs + bootHoldMs);
  const shouldTriggerHelp = crossed(previousClock, nextClock, bootProgressMs + bootHoldMs + helpDelayMs);
  const isRunning = nextClock > bootProgressMs + bootHoldMs + helpDelayMs;

  if (shouldPlayBootSound) playBootupSound();
  if (shouldTriggerHelp) {
    commands.help.execute(state);
    startCommandLoop();
  }

  state.startupClockMs = nextClock;
  state.renderLineTime += dt;

  const timeToRenderAll = state.lines.reduce((sum, line) => sum + timeToRenderLine(line), 0);
  state.renderLineTime = Math.min(state.renderLineTime, timeToRenderAll);

  const { nextLastShown, shouldClick } = computeNextScroll(state, true);
  if (shouldClick) {
    playClickSound();
  }
  state.lastLineShown = nextLastShown;

  const canvasRect = webglCanvas.getBoundingClientRect();

  // handle dpi and reset canvas
  {
    canvas.width = canvasRect.width * window.devicePixelRatio;
    canvas.height = canvasRect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    ctx.fillStyle = backgroundColors[state.color];
    ctx.fillRect(0, 0, canvasRect.width, canvasRect.height);
  }

  const lineSpacing = textHeight * 1;
  ctx.font = `${textHeight}px Apple II`;
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  const charWidth = ctx.measureText(' ').width;

  if (!state.fontsLoaded) return;

  const margin = textHeight * 2;
  ctx.translate(margin, margin);

  const frameEnv = {
    ctx,
    now,
    cycleTick,
    charWidth,
    textHeight,
    lineSpacing,
    loadTime,
    nextClock,
    cursorY: lineSpacing,
    onTypingChar() {
      playQuietTypingSound();
    },
  };

  let shouldShowPrompt = false;

  if (!state.focused) {
    ctx.fillText(`Click to start`, 0, frameEnv.cursorY);
  } else if (nextClock < bootProgressMs) {
    drawInitialLoadingBar(ctx, frameEnv);
  } else if (nextClock < bootProgressMs + bootHoldMs) {
    for (const line of [`${'█'.repeat(38)} 100%`, `Booting...`]) {
      ctx.fillText(line, 0, frameEnv.cursorY);
      frameEnv.cursorY += textHeight + lineSpacing;
    }
  } else if (nextClock < bootProgressMs + bootHoldMs + helpDelayMs) {
    shouldShowPrompt = true;
  } else {
    const reservePrompt = shouldReservePromptRow(state);
    const maxVisibleLines = computeMaxVisibleLines(canvasRect.height, margin, textHeight, lineSpacing, reservePrompt);
    const { linesToRender, baseGlobalIndex } = selectLinesToRender(state, maxVisibleLines);
    const cumulativeTimes = buildCumulativeTimes(state.lines);

    for (let i = 0; i < Math.min(linesToRender.length, state.lines.length); i++) {
      const line = linesToRender[i]!;
      if (i > linesToRender.length) break;

      const globalIndex = baseGlobalIndex + i;
      const lineBudgetMs = computeLineBudgetMs(state.renderLineTime, cumulativeTimes, globalIndex);

      const frameState = { x: 0, consumedMs: baselineTimePerLine, lineBudgetMs: lineBudgetMs, globalIndex };
      for (const token of line) {
        const remaining = frameState.lineBudgetMs - frameState.consumedMs;
        if (remaining <= 0 && tokensThatEatTime.includes(token.kind)) break;
        drawToken(token, frameEnv, frameState);
      }
      frameEnv.cursorY += textHeight + lineSpacing;
    }
  }

  // printing prompt
  if (isRunning) {
    shouldShowPrompt = state.lastLineShown === state.lines.length;
  }
  if (shouldShowPrompt && state.prompting) {
    const promptLine = `${promptMarker}${state.prompt}`;
    ctx.fillText(promptLine, 0, frameEnv.cursorY);

    const cursorVerticalShrink = textHeight * 0.2;
    const shouldShowCursor = Math.floor(performance.now() * 0.002) % 2 === 0;
    if (shouldShowCursor) {
      ctx.fillRect(
        charWidth * promptLine.length,
        frameEnv.cursorY + cursorVerticalShrink * 0.5,
        charWidth,
        textHeight - cursorVerticalShrink,
      );
    }
  }
}

async function startCommandLoop() {
  while (true) {
    await terminalPrompt(state, { cancellable: false });
    await attemptCommand(state);
  }
}
